import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFacility } from '../contexts/FacilityContext';
import FacilityFilter from '../components/FacilityFilter';
import ActiveFacilityBanner from '../components/ActiveFacilityBanner';
import { fetchARStats } from '../services/api';
import './Revenue.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n) =>
  '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

function fmtDate(s) {
  if (!s) return '—';
  const [y, m, d] = s.split('-');
  return `${parseInt(m)}/${parseInt(d)}/${y.slice(2)}`;
}

// ── AR Constants ─────────────────────────────────────────────────────────────

const AR_BUCKETS = [
  { key: 'b0_30',    label: '0–30 Days',   shortLabel: '0–30',   color: '#05998d' },
  { key: 'b31_60',   label: '31–60 Days',  shortLabel: '31–60',  color: '#3b82f6' },
  { key: 'b61_90',   label: '61–90 Days',  shortLabel: '61–90',  color: '#f59e0b' },
  { key: 'b91_120',  label: '91–120 Days', shortLabel: '91–120', color: '#f97316' },
  { key: 'b120plus', label: '120+ Days',   shortLabel: '120+',   color: '#ef4444' },
];

const AR_STATUS_CONFIG = {
  pending:   { color: '#f59e0b', bg: '#fffbeb', label: 'Pending' },
  submitted: { color: '#3b82f6', bg: '#eff6ff', label: 'Submitted' },
  denied:    { color: '#ef4444', bg: '#fef2f2', label: 'Denied' },
};

const EMPTY_BUCKET = { count: 0, billed: 0, collected: 0, adjustments: 0, outstanding: 0, patientResponsibility: 0 };

// ── Main Component ────────────────────────────────────────────────────────────

function AR() {
  const { selectedFacility, facilities } = useFacility();
  const [manualFilter, setManualFilter] = useState([]);
  const navigate = useNavigate();

  const [arData, setArData]       = useState(null);
  const [arLoading, setArLoading] = useState(true);
  const [arError, setArError]     = useState(null);
  const [selectedBucket, setSelectedBucket] = useState('all');
  const [denialsOnly, setDenialsOnly] = useState(false);

  const facilityId  = selectedFacility?.id ?? null;
  const facilityIds = !selectedFacility && manualFilter.length > 0 ? manualFilter : null;

  useEffect(() => {
    setArLoading(true);
    setArError(null);
    setSelectedBucket('all');
    fetchARStats(facilityId, facilityIds)
      .then((res) => setArData(res.data))
      .catch(() => setArError('Failed to load AR data. Please try again.'))
      .finally(() => setArLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, manualFilter.join(',')]);

  // ── Derived Values ──────────────────────────────────────────────────────────

  const arKpis = arData?.kpis ?? {
    totalAR: 0, daysInAR: 0, arOver90Pct: 0, arOver90Amount: 0, totalOutstandingClaims: 0,
  };

  const arBuckets = arData?.buckets ?? {
    b0_30: EMPTY_BUCKET, b31_60: EMPTY_BUCKET, b61_90: EMPTY_BUCKET,
    b91_120: EMPTY_BUCKET, b120plus: EMPTY_BUCKET,
  };

  const denialCount = useMemo(
    () => (arData?.claims ?? []).filter((c) => c.paymentStatus === 'denied').length,
    [arData]
  );

  const filteredARClaims = useMemo(() => {
    let claims = arData?.claims ?? [];
    if (denialsOnly)              claims = claims.filter((c) => c.paymentStatus === 'denied');
    if (selectedBucket !== 'all') claims = claims.filter((c) => c.agingBucket === selectedBucket);
    return claims;
  }, [arData, selectedBucket, denialsOnly]);

  const arByPayer           = arData?.byPayer ?? [];
  const maxPayerOutstanding = Math.max(...arByPayer.map((p) => p.outstanding), 1);

  const daysInARColor = arKpis.daysInAR === 0 ? '#6b7280'
    : arKpis.daysInAR < 30 ? '#05998d'
    : arKpis.daysInAR < 50 ? '#f59e0b'
    : '#ef4444';

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="revenue-page">
      <ActiveFacilityBanner />

      <div className="revenue-header">
        <div>
          <h1 className="revenue-title">Accounts Receivable</h1>
          <p className="revenue-subtitle">
            {selectedFacility
              ? `Outstanding AR aging for ${selectedFacility.name}`
              : 'Outstanding AR aging across all facilities'}
          </p>
        </div>
        {!selectedFacility && (
          <FacilityFilter
            selectedIds={manualFilter}
            onChange={setManualFilter}
            facilities={facilities}
          />
        )}
      </div>

      {arError && <div className="revenue-error">{arError}</div>}

      {/* ── AR KPI Cards ── */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-icon" style={{ background: '#fef2f2', color: '#ef4444' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Total AR</span>
            <span className="kpi-value">{arLoading ? '—' : fmt(arKpis.totalAR)}</span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon" style={{ background: '#fff7ed', color: daysInARColor }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Days in AR</span>
            <span className="kpi-value" style={!arLoading ? { color: daysInARColor } : {}}>
              {arLoading ? '—' : arKpis.daysInAR.toFixed(1)}
            </span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon" style={{ background: '#fff7ed', color: '#f97316' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">AR &gt; 90 Days</span>
            <span
              className="kpi-value"
              style={!arLoading && arKpis.arOver90Pct > 25 ? { color: '#ef4444' } : {}}
            >
              {arLoading ? '—' : `${arKpis.arOver90Pct.toFixed(1)}%`}
            </span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon" style={{ background: '#fef2f2', color: '#dc2626' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">AR &gt; 90 Days ($)</span>
            <span
              className="kpi-value"
              style={!arLoading && arKpis.arOver90Amount > 0 ? { color: '#dc2626' } : {}}
            >
              {arLoading ? '—' : fmt(arKpis.arOver90Amount)}
            </span>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon" style={{ background: '#eff6ff', color: '#3b82f6' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          </div>
          <div className="kpi-content">
            <span className="kpi-label">Outstanding Claims</span>
            <span className="kpi-value">{arLoading ? '—' : arKpis.totalOutstandingClaims.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* ── Aging Breakdown Grid ── */}
      <div className="chart-card aging-card">
        <div className="chart-card-header">
          <div>
            <h3 className="chart-title">Aging Breakdown</h3>
            <p className="chart-desc">Click a bucket to filter the claims table below</p>
          </div>
          {selectedBucket !== 'all' && (
            <button className="interval-pill active" onClick={() => setSelectedBucket('all')}>
              Clear filter
            </button>
          )}
        </div>
        {arLoading ? (
          <div className="chart-loading">Loading…</div>
        ) : (
          <div className="aging-grid">
            {AR_BUCKETS.map((bucket) => {
              const data    = arBuckets[bucket.key];
              const pct     = arKpis.totalAR > 0
                ? ((data.outstanding / arKpis.totalAR) * 100).toFixed(1)
                : '0.0';
              const isActive = selectedBucket === bucket.key;
              return (
                <button
                  key={bucket.key}
                  className={`aging-bucket-card${isActive ? ' active' : ''}`}
                  style={{
                    '--bucket-color': bucket.color,
                    background: isActive ? `${bucket.color}12` : 'white',
                  }}
                  onClick={() => setSelectedBucket(bucket.key)}
                >
                  <span className="aging-bucket-label">{bucket.label}</span>
                  <span className="aging-bucket-amount">{fmt(data.outstanding)}</span>
                  <div className="aging-bucket-meta">
                    <span className="aging-bucket-count">{data.count} claim{data.count !== 1 ? 's' : ''}</span>
                    <span
                      className="aging-bucket-pct"
                      style={{ background: `${bucket.color}18` }}
                    >{pct}%</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Outstanding Claims Table ── */}
      <div className="chart-card">
        <div className="ar-table-header">
          <div>
            <h3 className="chart-title">{denialsOnly ? 'Denial Work Queue' : 'Outstanding Claims'}</h3>
            <p className="chart-desc" style={{ marginBottom: 0 }}>
              {arLoading ? 'Loading…' : (
                `${filteredARClaims.length} claim${filteredARClaims.length !== 1 ? 's' : ''}`
                + (denialsOnly ? ' denied' : ' outstanding')
                + (selectedBucket === 'all' ? '' : ` · ${AR_BUCKETS.find((b) => b.key === selectedBucket)?.label}`)
              )}
            </p>
          </div>
          <div className="ar-bucket-pills">
            {/* Denial follow-up queue: filter the AR table to denied claims only */}
            <button
              className={`interval-pill${denialsOnly ? ' active' : ''}`}
              onClick={() => setDenialsOnly((v) => !v)}
              style={denialsOnly
                ? { background: '#ef4444', borderColor: '#ef4444', color: '#fff' }
                : { color: '#dc2626', borderColor: '#fca5a5' }}
              title="Show only denied claims that need follow-up"
            >
              Denials{denialCount > 0 ? ` (${denialCount})` : ''}
            </button>
            <span style={{ width: 1, alignSelf: 'stretch', background: '#e5e7eb', margin: '0 2px' }} />
            <button
              className={`interval-pill${selectedBucket === 'all' ? ' active' : ''}`}
              onClick={() => setSelectedBucket('all')}
            >All</button>
            {AR_BUCKETS.map((bucket) => (
              <button
                key={bucket.key}
                className={`interval-pill${selectedBucket === bucket.key ? ' active' : ''}`}
                onClick={() => setSelectedBucket(bucket.key)}
              >
                {bucket.shortLabel}
              </button>
            ))}
          </div>
        </div>

        {arLoading ? (
          <div className="chart-loading">Loading…</div>
        ) : filteredARClaims.length === 0 ? (
          <div className="chart-empty">
            {selectedBucket === 'all' ? 'No outstanding claims' : 'No claims in this aging bucket'}
          </div>
        ) : (
          <div className="ar-table-wrapper">
            <table className="ar-table">
              <thead>
                <tr>
                  <th>Claim #</th>
                  <th>Patient</th>
                  <th>Payer</th>
                  <th>Svc Date</th>
                  <th>Submitted</th>
                  <th>Days Out</th>
                  <th className="num">Billed</th>
                  <th className="num">Contractual</th>
                  {/* Step 13 (S13-e): OA/PI itemized for the biller — adjudicated away, nothing to chase */}
                  <th className="num">Other Adj.</th>
                  <th className="num">Outstanding</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredARClaims.map((claim) => {
                  const bucket     = AR_BUCKETS.find((b) => b.key === claim.agingBucket);
                  const statusConf = AR_STATUS_CONFIG[claim.paymentStatus]
                    ?? { color: '#6b7280', bg: '#f9fafb', label: claim.paymentStatus };
                  return (
                    <tr
                      key={claim.id}
                      className="ar-table-row"
                      onClick={() => navigate(`/claims/${claim.id}`)}
                      title="Click to view claim details"
                    >
                      <td className="ar-claim-number">
                        {claim.claimNumber}
                        {claim.paymentStatus === 'denied' && claim.denialReason && (
                          <span
                            className="ar-denial-reason"
                            style={{
                              display: 'block', marginTop: 3, fontSize: 11, fontWeight: 600,
                              color: '#b91c1c', whiteSpace: 'normal', lineHeight: 1.3,
                            }}
                            title={claim.denialReason}
                          >
                            {claim.denialReason}
                          </span>
                        )}
                      </td>
                      <td>{claim.patientName}</td>
                      <td className="ar-payer-cell">{claim.payerName}</td>
                      <td className="ar-date-cell">{fmtDate(claim.serviceDate)}</td>
                      <td className="ar-date-cell">{fmtDate(claim.submittedDate)}</td>
                      <td>
                        <span
                          className="ar-days-badge"
                          style={{ color: bucket?.color, background: `${bucket?.color}18` }}
                        >
                          {claim.daysOutstanding}d
                        </span>
                      </td>
                      <td className="ar-amount">{fmt(claim.totalCharge)}</td>
                      <td className="ar-amount ar-adj">{claim.contractualAdj > 0 ? fmt(claim.contractualAdj) : '—'}</td>
                      <td className="ar-amount ar-adj">{claim.otherAdj > 0 ? fmt(claim.otherAdj) : '—'}</td>
                      <td className="ar-amount ar-outstanding">{fmt(claim.outstanding)}</td>
                      <td>
                        <span
                          className="ar-status-badge"
                          style={{ color: statusConf.color, background: statusConf.bg }}
                        >
                          {statusConf.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── AR by Payer ── */}
      {!arLoading && arByPayer.length > 0 && (
        <div className="chart-card">
          <h3 className="chart-title">AR by Payer</h3>
          <p className="chart-desc">Outstanding balances by insurance payer</p>
          <div className="facility-bars">
            {arByPayer.slice(0, 10).map((payer, i) => (
              <div key={i} className="facility-bar-row">
                <span className="facility-bar-name">{payer.payerName}</span>
                <div className="facility-bar-track">
                  <div
                    className="facility-bar-fill"
                    style={{
                      width: `${(payer.outstanding / maxPayerOutstanding) * 100}%`,
                      background: 'linear-gradient(90deg, #ef4444, #dc2626)',
                    }}
                  />
                </div>
                <div className="facility-bar-values">
                  <span className="facility-bar-amount">{fmt(payer.outstanding)}</span>
                  <span className="facility-bar-collected-amt">{payer.count} claim{payer.count !== 1 ? 's' : ''}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AR;
